import { useAuth } from '../contexts/AuthContext';
import { moduleConfigAPI } from '../services/api';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useEntitlements } from './useEntitlements';
import { ENTITLEMENT_MODULE_ALIASES } from '../config/navigation';

// Default all modules to enabled
// Role names are the CANONICAL SPANISH strings the backend sends in
// user_condo_roles.role: super_admin, admin, directive, resident, security,
// provider, empleado, maintenance_staff.
// Audit (Jaime 2026-09-07): the former 'employee' entries were DEAD — the
// backend never sends English 'employee', so they never matched. They are
// normalized to 'empleado' (same intended access), not deleted, so employee
// access to dashboard/qr/panic/home_tareas/configuracion actually works.
const DEFAULT_MODULE_PERMISSIONS: Record<string, string[]> = {
  'dashboard': ['super_admin', 'admin', 'directive', 'resident', 'empleado', 'maintenance_staff', 'security', 'provider'],
  // F10: condo analytics are limited to administrators and directivos; residents and operational roles are excluded.
  'analytics': ['super_admin', 'admin', 'directive', 'treasurer'],
  'finanzas': ['super_admin', 'admin', 'directive', 'resident'],
  'ingresos': ['super_admin', 'admin'],
  // (2026-09-24, bug #129) 'propietario' restored — backend requireRoleForExpenses
  // explicitly allows it; standalone-owner persona was FE-denied. 'resident' kept:
  // the server still 403s plain residents server-side (kimi advisory).
  'gastos': ['super_admin', 'admin', 'directive', 'resident', 'propietario'],
  'cotizaciones': ['super_admin', 'admin', 'directive', 'resident'],
  'pagos': ['resident'],
  'morosos': ['super_admin', 'admin', 'directive', 'resident'],
  'amenidades': ['super_admin', 'admin', 'directive', 'resident'],
  'calendario': ['super_admin', 'admin', 'directive', 'resident'],
  'quejas': ['super_admin', 'admin', 'directive', 'resident'],
  'anuncios': ['super_admin', 'admin', 'directive', 'resident', 'maintenance_staff'],
  'votaciones': ['super_admin', 'admin', 'directive', 'resident'],
  'qr_acceso': ['super_admin', 'admin', 'directive', 'resident', 'empleado', 'maintenance_staff', 'security'],
  'panic_button': ['super_admin', 'admin', 'directive', 'resident', 'empleado', 'maintenance_staff', 'security'],
  'visitantes': ['super_admin', 'admin', 'security'],
  'emergencias': ['super_admin', 'admin', 'directive', 'resident', 'security'],
  'home_tareas': ['resident', 'empleado'],
  'home_compras': ['resident'],
  'home_mascotas': ['resident'],
  'home_vehiculos': ['resident'],
  'home_presupuesto': ['resident'],
  'mantenimiento': ['super_admin', 'admin', 'maintenance_staff'],
  'proveedor': ['provider'],
  'configuracion': ['super_admin', 'admin', 'directive', 'resident', 'empleado', 'maintenance_staff', 'security', 'provider'],
};

// Module ID mapping from menu items to module IDs
const MODULE_ID_MAP: Record<string, string> = {
  'qr': 'qr_acceso',
  'verify': 'qr_acceso',
  'voting': 'votaciones',
  'calendar': 'calendario',
  'announcements': 'anuncios',
  'amenities': 'amenidades',
  'financial': 'finanzas',
  'work-orders': 'mantenimiento',
  'quejas': 'quejas',
  'emergencias': 'emergencias',
  'reglamentos': 'configuracion',
  'caja-chica': 'finanzas',
  'casetas': 'mantenimiento',
  'empresas-seguridad': 'configuracion',
  'personal-seguridad': 'configuracion',
  'reporte-visitas': 'visitantes',
  'info-condominio': 'configuracion',
  'llave-digital': 'qr_acceso',
  'tasks': 'home_tareas',
  'shopping': 'home_compras',
  'pets': 'home_mascotas',
  'vehicles': 'home_vehiculos',
  'budget': 'home_presupuesto',
  'openpay-config': 'configuracion',
  'module-config': 'configuracion',
  'admin': 'configuracion',
  'dashboard': 'dashboard',
};

export const useModuleAccess = () => {
  const { user } = useAuth();
  const [moduleConfig, setModuleConfig] = useState<Record<string, boolean>>({});
  // REVISION (2026-10-01, peer-review NO-GO fix): raw per-condo module rows
  // exactly as the backend GET /condos/:id/modules returned them (is_active +
  // source), BEFORE the missing-module defaults are applied. hasModuleAccess
  // arms the module-toggle override ONLY on rows whose source is an EXPLICIT
  // condo decision ('override' from the toggles UI, or 'legacy_config' from an
  // explicit condo_module_config row). Rows with source 'global_default'
  // (platform default / resolver fail-open fallback) or a missing source
  // (older payloads) do NOT override: they fall through to the plan-tier
  // entitlement resolver exactly as before, so free-plan condos whose
  // /modules endpoint returns all-active DEFAULT_MODULES stay gated.
  interface RawModuleRow { is_active: boolean; source?: string }
  const rawConfigRef = useRef<Record<string, RawModuleRow>>({});
  const [loading, setLoading] = useState(true);
  const { entitlement, entitlementReady } = useEntitlements();

  useEffect(() => {
    const fetchModuleConfig = async () => {
      if (!user?.condoId) {
        // Try localStorage when not logged in or no condo
        const cached = localStorage.getItem('moduleConfig');
        if (cached) {
          try {
            setModuleConfig(JSON.parse(cached));
          } catch (e) {
            console.error('Error parsing cached module config:', e);
          }
        }
        // No active condo: cached config is merged/defaulted; treat as no
        // explicit rows (entitlement resolver decides).
        rawConfigRef.current = {};
        setLoading(false);
        return;
      }
      
      try {
        const response = await moduleConfigAPI.getAll(user.condoId);
        const config: Record<string, boolean> = {};
        const mods = Array.isArray(response.data) ? response.data : [];
        const rawRows: Record<string, RawModuleRow> = {};
        mods.forEach((mod: any) => {
          config[mod.module_id] = mod.is_active;
          // Keep source per row: only explicit condo decisions ('override' |
          // 'legacy_config') may arm the toggle override in hasModuleAccess.
          rawRows[mod.module_id] = { is_active: !!mod.is_active, source: mod.source };
        });
        // Raw snapshot with source provenance for the module-toggle override
        // in hasModuleAccess (2026-10-01, peer-review revision).
        rawConfigRef.current = rawRows;
        
        // Apply defaults for missing modules
        Object.keys(DEFAULT_MODULE_PERMISSIONS).forEach(key => {
          if (config[key] === undefined) {
            config[key] = true;
          }
        });
        
        setModuleConfig(config);
        // Cache in localStorage
        localStorage.setItem('moduleConfig', JSON.stringify(config));
      } catch (error) {
        console.error('Error fetching module config:', error);
        // Try localStorage fallback
        const cached = localStorage.getItem('moduleConfig');
        if (cached) {
          try {
            setModuleConfig(JSON.parse(cached));
          } catch (e) {
            console.error('Error parsing cached module config:', e);
            setAllEnabled();
          }
        } else {
          setAllEnabled();
        }
        // Degrade path: no explicit per-condo rows -> entitlement resolver
        // keeps deciding (module-toggle override not armed).
        rawConfigRef.current = {};
      } finally {
        setLoading(false);
      }
    };

    const setAllEnabled = () => {
      const defaultConfig: Record<string, boolean> = {};
      Object.keys(DEFAULT_MODULE_PERMISSIONS).forEach(key => {
        defaultConfig[key] = true;
      });
      setModuleConfig(defaultConfig);
    };

    fetchModuleConfig();
  }, [user?.condoId]);

  const hasModuleAccess = useCallback((moduleId: string): boolean => {
    if (!user) return false;
    
    // Map the menu item ID to module ID
    const mappedModuleId = MODULE_ID_MAP[moduleId] || moduleId;
    
    // REVISION (2026-10-01, peer-review NO-GO fix): the module-toggle
    // override arms ONLY on an explicit condo decision. The /modules
    // endpoint falls back to DEFAULT_MODULES (all-active) for zero-row condos
    // and rewrites is_active with fail-open 'global_default', so mere key
    // presence is NOT proof of a condo decision. Gating matrix:
    //   source 'override' | 'legacy_config', is_active true  -> allow
    //     (subject to the DEFAULT_MODULE_PERMISSIONS role check)
    //   source 'override' | 'legacy_config', is_active false -> deny
    //   source 'global_default' | missing source | missing key -> no
    //     override; fall through to the plan-tier entitlement resolver below.
    const rawRow = rawConfigRef.current[mappedModuleId];
    const explicitCondoDecision =
      rawRow !== undefined &&
      (rawRow.source === 'override' || rawRow.source === 'legacy_config');
    if (explicitCondoDecision) {
      if (!rawRow.is_active) return false;
      const roles = DEFAULT_MODULE_PERMISSIONS[mappedModuleId] || [];
      return roles.length === 0 || roles.includes(user.role);
    }

    // Plan gating is exclusively resolver-backed. JWT modules, /me/permissions,
    // and the legacy module-config endpoint are not entitlement sources.
    if (!entitlementReady || !entitlement) return false;
    const planModule = Object.keys(MODULE_ID_MAP).find((key) => MODULE_ID_MAP[key] === mappedModuleId) || moduleId;
    const accepted = ENTITLEMENT_MODULE_ALIASES[planModule] || [planModule, mappedModuleId];
    if (!accepted.some((module) => entitlement.modules?.includes(module))) return false;
    const allowedRoles = DEFAULT_MODULE_PERMISSIONS[mappedModuleId] || [];
    return allowedRoles.length === 0 || allowedRoles.includes(user.role);
  }, [user, moduleConfig, entitlement, entitlementReady]);

  const hasPermission = useCallback((action: string, resource: string): boolean => {
    if (!user) return false;
    
    // Super admin has all permissions
    if (user.role === 'super_admin') return true;
    
    // Check module access first
    if (!hasModuleAccess(resource)) return false;
    
    // Role-based permission logic (English role names matching backend)
    const rolePermissions: Record<string, string[]> = {
      'admin': ['view', 'create', 'edit', 'delete'],
      'directive': ['view', 'approve'],
      'resident': ['view', 'create', 'edit_own', 'delete_own'],
      'empleado': ['view', 'complete_task'],
      'maintenance_staff': ['view', 'complete_task'],
      'security': ['view', 'verify', 'register'],
      'provider': ['view_own', 'edit_own'],
    };
    
    const userPermissions = rolePermissions[user.role] || [];
    return userPermissions.includes(action) || userPermissions.includes(`${action}_own`);
  }, [user, hasModuleAccess]);

  // Check if user can manage residents in a specific residence
  const canManageResidence = useCallback((residenceId: string): boolean => {
    if (!user) return false;
    
    // Super admin and condo admin can manage any residence
    if (['super_admin', 'admin'].includes(user.role)) return true;
    
    // Resident can only manage their own residence
    // @ts-ignore - residenceId may be added to user type
    if (user.role === 'resident') {
      // @ts-ignore
      return user.residenceId === residenceId || user.unitId === residenceId;
    }
    
    return false;
  }, [user]);

  // Check if user can become a supplier
  const canBecomeSupplier = useCallback((): boolean => {
    if (!user) return false;
    // Only residents with paid subscription
    if (user.role !== 'resident') return false;
    // @ts-ignore - subscriptionTier may be added to user type
    return ['basic', 'premium'].includes(user.subscriptionTier || user.plan);
  }, [user]);

  return {
    hasModuleAccess,
    hasPermission,
    canManageResidence,
    canBecomeSupplier,
    moduleConfig,
    loading,
  };
};

export default useModuleAccess;
